import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { toTitleCase } from 'codelyzer/util/utils';
import { isValid } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { BeamerUrlService } from '../../root/beamer-url.service';
import { ExportService } from '../../root/export.service';
import { NotificationsService } from '../../root/notifications.service';
import { RegionService } from '../../root/region.service';
import { ConfirmActionDialogComponent } from '../../shared/confirm-action-dialog/confirm-action-dialog.component';
import { SnackBarService } from '../../shared/snackbar.service';
import {
  Alert,
  BonusEvent,
  DriversHitchStats,
  FracActivityReportPerRegion,
  LastSettlementFees,
  NotificationType,
  OwnerDashboardStats,
  Region,
  TierFeedsResponse,
  TodayDemand,
} from '../../types';
import { OwnerDashboardService } from '../owner-dashboard.service';

@Component({
  selector: 'lohi-owner-dashboard-main',
  templateUrl: './owner-dashboard-main.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OwnerDashboardMainComponent implements OnInit, OnDestroy {
  @ViewChild('crewModal') private crewsModalTemplate: TemplateRef<any>;
  private destroy$$ = new Subject();
  public stats$: Observable<OwnerDashboardStats>;
  public todayDemand$: Observable<TodayDemand[]>;
  public safeNewsFeedUrl: SafeResourceUrl;
  public regionsFilterControl = new FormControl('all');
  public fracsActivity$: Observable<FracActivityReportPerRegion[]>;
  public selectedFracActivity$$ = new BehaviorSubject<FracActivityReportPerRegion>(null);
  public exporting$$ = new BehaviorSubject<boolean>(false);
  public exportFormGroup = new FormGroup({
    start: new FormControl(null, [Validators.required]),
    end: new FormControl(null, [Validators.required]),
  });
  public regions$: Observable<Region[]>;
  public tierFeeds$: Observable<TierFeedsResponse>;
  public tierNotificationOn$$ = new BehaviorSubject(false);
  public bonusEvents$: Observable<BonusEvent[]>;
  public finesAndFees$: Observable<LastSettlementFees>;
  public driversHitchStats$: Observable<DriversHitchStats>;
  public tierDropsEnabled$: Observable<NotificationType>;
  public tierDropControl = new FormControl();
  public alerts$: Observable<Alert[]>;
  public clearingAllAlerts$$ = new BehaviorSubject<boolean>(false);

  constructor(
    private dashboardService: OwnerDashboardService,
    private sanitizer: DomSanitizer,
    private exportService: ExportService,
    private logger: NGXLogger,
    private snackBar: SnackBarService,
    private cd: ChangeDetectorRef,
    private beamerUrlService: BeamerUrlService,
    private regionService: RegionService,
    private dialog: MatDialog,
    private notiService: NotificationsService,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.stats$ = this.dashboardService.stats$;
    this.bonusEvents$ = this.dashboardService.bonusEvents$;
    this.todayDemand$ = this.dashboardService.todayDemand$;
    this.alerts$ = this.dashboardService.alerts$.pipe(map((alerts) => alerts?.filter((alert) => !alert.dismissed)));
    this.finesAndFees$ = this.dashboardService.lastFinesAndFeesThrottle$;
    this.fracsActivity$ = this.dashboardService.fracsActivity$.pipe(
      filter((value) => !!value),
      tap((value) => this.selectedFracActivity$$.next(value[0])),
    );
    this.regions$ = this.regionService.regions$;
    this.tierFeeds$ = this.dashboardService.tierFeeds$;
    this.driversHitchStats$ = this.dashboardService.driversHitchStats$;
    this.tierDropsEnabled$ = this.notiService.notificationTypes$.pipe(
      filter((value) => !!value),
      map((value) => value.filter((value1) => value1.name === 'driver_tier_drop')[0]),
    );
    this.getBeamerUrl('all');
    this.setupFormControls();
    this.setupRealControls();
  }

  private setupFormControls() {
    this.regionsFilterControl.valueChanges.pipe(takeUntil(this.destroy$$)).subscribe((value) => {
      this.getBeamerUrl(value);
    });
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public selectFracActivity(region: FracActivityReportPerRegion): FracActivityReportPerRegion {
    this.selectedFracActivity$$.next(region);
    return region;
  }

  public async exportData() {
    if (this.exporting$$.value) {
      return;
    }
    if (this.exportFormGroup.invalid) {
      this.exportFormGroup.markAllAsTouched();
      return;
    }
    const value: { start: Date; end: Date } = this.exportFormGroup.value;
    if (!isValid(value.start) || !isValid(value.end)) {
      return;
    }
    this.exporting$$.next(true);
    this.exportService.getBulkExportUrl$(value.start, value.end).subscribe(
      (urls) => {
        for (const url of urls) {
          window.open(url);
        }
        this.resetExportForm();
        this.exportFormGroup.markAsUntouched();
        this.exporting$$.next(false);
      },
      (error) => {
        this.logger.error(error);
        this.snackBar.showError('Failed to retrieve export');
        this.exporting$$.next(false);
      },
    );
  }

  public resetExportForm() {
    this.exportFormGroup = new FormGroup({
      start: new FormControl(null, [Validators.required]),
      end: new FormControl(null, [Validators.required]),
    });
    this.cd.markForCheck();
  }

  private getBeamerUrl(segmentFilter: string) {
    this.beamerUrlService
      .getUrl$(segmentFilter)
      .pipe(take(1))
      .subscribe((resp) => {
        if (resp) {
          this.safeNewsFeedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(resp.url);
          this.cd.detectChanges();
        }
      });
  }

  public getTierImage(tierId: number): string {
    switch (tierId) {
      case 1:
        return '/assets/images/tier-blue.svg';
      case 2:
        return '/assets/images/tier-silver.svg';
      case 3:
        return '/assets/images/tier-gold.svg';
      case 4:
        return '/assets/images/tier-black.svg';
      default:
        return '/assets/images/tier-blue.svg';
    }
  }

  public getTierColor(tierId: number): string {
    switch (tierId) {
      case 1:
        return '#448AFF';
      case 2:
        return '#6B767C';
      case 3:
        return '#AD8C3C';
      case 4:
        return '#2D2D2E';
      default:
        return '#448AFF';
    }
  }

  public toggleNotification() {
    const onOrOff = this.tierNotificationOn$$.value ? 'off' : 'on';
    const dialog = this.dialog.open(ConfirmActionDialogComponent, {
      data: {
        title: 'Turn ' + onOrOff + ' SMS Notifications?',
        desc: 'Are you sure you want to turn ' + onOrOff + ' notifications about tier changes?',
        cancelButtonText: 'Cancel',
        submitButtonText: 'Turn ' + toTitleCase(onOrOff),
      },
    });

    dialog.afterClosed().subscribe((keepGoing) => {
      if (!keepGoing) {
        return;
      }
      this.tierNotificationOn$$.next(!this.tierNotificationOn$$.value);
    });
  }

  public openCrewsModal() {
    this.dialog.open(this.crewsModalTemplate, {
      autoFocus: false,
    });
  }

  private async setupRealControls() {
    const enabled = await this.tierDropsEnabled$.pipe(take(1)).toPromise();
    this.tierDropControl.setValue(enabled.enabled);

    this.tierDropControl.valueChanges.pipe(takeUntil(this.destroy$$)).subscribe((value) => {
      this.notiService.toggleNotification(enabled.id, value);
      this.snackBar.showMessage(`Driver Tier Notifications ${value ? 'Enabled' : 'Disabled'}`);
    });
  }

  public toggleTierNotification() {
    this.tierDropControl.setValue(!this.tierDropControl.value);
  }

  public dismissNotification(ids: number[]) {
    this.dashboardService.dismissAlerts$(ids).subscribe(
      (value) => {
        this.snackBar.showMessage('Alert Dismissed');
      },
      (error) => {
        this.snackBar.showError(error.error.messageToUser);
      },
    );
  }

  public dismissNotificationLocally(id) {
    this.dashboardService.dismissAlertLocally(id);
  }

  public clearAll() {
    this.clearingAllAlerts$$.next(true);
    this.dashboardService.clearAllAlerts$().subscribe(
      (value) => {
        this.clearingAllAlerts$$.next(false);
        this.snackBar.showMessage('All alerts cleared');
      },
      (error) => {
        this.clearingAllAlerts$$.next(false);
        this.snackBar.showError(error.error.messageToUser);
      },
    );
  }

  public redirectToDriverScorecard(id: string) {
    this.router.navigate(['drivers', id, 'scorecard']);
  }

  public getPenaltyAlertDescription(description: string): string {
    return description
      .split(' ')
      .splice(0, description.split(' ').length - 1)
      .join(' ');
  }

  public getPenaltyAlertDescriptionDriverNumber(description: string): string {
    return description.split(' ').splice(-1).join(' ');
  }

  public getPenaltyAlertTitleTrailerNumber(title: string): string {
    return title.split(' ').splice(0, 2).join(' ');
  }

  public getPenaltyAlertTitle(title: string): string {
    return title.split(' ').splice(2).join(' ');
  }
}
